import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './Bank.css';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { getEffectiveSoundOutputVolume } from './hooks/soundEffectsManager';
import { API_URL } from './config/apiConfig';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox } from './tutorial/index';
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import { buildFlashlightSwatchBackground, normalizeFlashlightColorForDisplay } from './constants/flashlightColors';

// Pricing based on rarity
const RARITY_PRICES = {
  common: 3000,
  rare: 6000,
  epic: 10000,
  legendary: 30000
};

// Feature images - same as Profile.js
const preparationBonusFeatures = [
  { key: 'plan_ahead', image: '/images/plan_ahead.jpg', altI18nKey: 'bank.altPlanAhead' },
  { key: 'black_market', image: '/images/black_market_dossier.jpg', altI18nKey: 'bank.altBlackMarketDossier' },
  { key: 'team_leader', image: '/images/team_leader_briefing.jpg', altI18nKey: 'bank.altTeamLeaderBriefing' }
];

const diplomaticAccordsFeatures = [
  { key: 'strategic_coordination', image: '/images/strategic_coordination.jpg', altI18nKey: 'bank.altStrategicCoordination' },
  { key: 'public_relations', image: '/images/public_relations.jpg', altI18nKey: 'bank.altPublicRelations' },
  { key: 'emergency_protocol', image: '/images/emergency_protocol.jpg', altI18nKey: 'bank.altEmergencyProtocol' },
  { key: 'command_directive', image: '/images/command_directive.jpg', altI18nKey: 'bank.altCommandDirective' }
];

/** War Effort HQ base building id → art (same paths as War Effort fortification). */
const BANK_GAMBLE_WAR_BUILDING_IMAGES = {
  eclipse_array: '/images/array.png',
  command_center: '/images/command.png',
  cattle_ranch: '/images/cattle.png',
  quarry: '/images/stone.png',
  sewers: '/images/sewers.png',
  portal: '/images/erray.png',
  lavender_field: '/images/lavender.png',
  intelligence_agency: '/images/intel.png',
  bank: '/images/bank.png',
  research_center: '/images/research.png',
  hospital: '/images/hospital.png',
  airfield: '/images/airfield.png',
  armory: '/images/armory.png',
  drone_hangar: '/images/dronehangar.png',
  communications_hub: '/images/comhub.png',
};

const BANK_RARITY_KEYS = ['common', 'rare', 'epic', 'legendary'];

/** Client-only dev preview when catalog fetch fails; does not grant anything. */
const DEV_BANK_EPIC_PREVIEW_FALLBACK_ORDER = {
  id: 1,
  name: 'Epic preview',
  rarity: 'epic',
  faction_name: "Ra's Children",
  faction_id: 3,
  cost: 5,
  attack_rating: 4,
  health: 6,
  type: 'Unit',
  abilities: ['Recover'],
  description: '',
};

function bankRarityLabel(rarity, t) {
  const key = String(rarity || 'common').toLowerCase();
  if (!BANK_RARITY_KEYS.includes(key)) {
    throw new Error(`Unknown bank order rarity for localization: ${JSON.stringify(rarity)}`);
  }
  return t(`bank.${key}`);
}

function Bank({ user, onLogout }) {
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation();
  const isDevPlayer1 = user === 'player1';
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [billyBucks, setBillyBucks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseMessage, setPurchaseMessage] = useState(null);
  const [gambling, setGambling] = useState(false);
  const [gambleResult, setGambleResult] = useState(null);
  const [achievementUnlocked, setAchievementUnlocked] = useState(null);
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();
  const [purchaseMode, setPurchaseMode] = useState('byOrder'); // 'byOrder' or 'completeStrategy'
  const [incompleteStrategies, setIncompleteStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [missingOrders, setMissingOrders] = useState([]);
  const [loadingStrategies, setLoadingStrategies] = useState(false);
  const [orderDuplicators, setOrderDuplicators] = useState({
    common: 0,
    rare: 0,
    epic: 0,
    legendary: 0
  });
  const [converting, setConverting] = useState(false);
  const [convertMessage, setConvertMessage] = useState(null);
  const [showTutorialBank, setShowTutorialBank] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  /** Mobile only: which main bank panel is visible (replaces stacked sections + 2-button mode row). */
  const [mobileBankTab, setMobileBankTab] = useState('buyOrder');
  /** Robot teller speech bubble — short reactive line that auto-clears back to idle. */
  const [robotMessageKey, setRobotMessageKey] = useState('idle');
  const robotMessageTimerRef = useRef(null);

  const sayRobotMessage = (key) => {
    if (robotMessageTimerRef.current) {
      clearTimeout(robotMessageTimerRef.current);
      robotMessageTimerRef.current = null;
    }
    setRobotMessageKey(key);
    robotMessageTimerRef.current = setTimeout(() => {
      setRobotMessageKey('idle');
      robotMessageTimerRef.current = null;
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (robotMessageTimerRef.current) clearTimeout(robotMessageTimerRef.current);
    };
  }, []);
  useMusic(MAIN_MENU_MUSIC_PATH);

  useEffect(() => {
    if (user && !loading && shouldShowTutorial('bank')) {
      const timer = setTimeout(() => setShowTutorialBank(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, shouldShowTutorial]);

  // Fetch all orders and Billy Bucks balance
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No token found');
          return;
        }

        // Fetch orders
        const currentLanguage = i18nInstance.language || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const ordersRes = await fetch(`${API_URL}/bank/orders?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': normalizedLanguage,
          },
        });

        if (ordersRes.ok) {
          const ordersData = await ordersRes.json();
          setOrders(ordersData);
          setFilteredOrders(ordersData);
        } else {
          console.error('Failed to fetch orders:', ordersRes.status);
        }

        // Fetch Billy Bucks balance
        const consumablesRes = await fetch(`${API_URL}/profile/consumables`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (consumablesRes.ok) {
          const consumablesData = await consumablesRes.json();
          setBillyBucks(consumablesData.billyBucks || 0);
          setOrderDuplicators(consumablesData.duplicators || {
            common: 0,
            rare: 0,
            epic: 0,
            legendary: 0
          });
        } else {
          console.error('Failed to fetch consumables:', consumablesRes.status);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch incomplete strategies when switching to completeStrategy mode
  useEffect(() => {
    if (purchaseMode === 'completeStrategy') {
      const fetchIncompleteStrategies = async () => {
        try {
          setLoadingStrategies(true);
          const token = localStorage.getItem('token');
          if (!token) return;

          const res = await fetch(`${API_URL}/bank/incomplete-strategies`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.ok) {
            const strategies = await res.json();
            setIncompleteStrategies(strategies);
          } else {
            console.error('Failed to fetch incomplete strategies');
          }
        } catch (err) {
          console.error('Error fetching incomplete strategies:', err);
        } finally {
          setLoadingStrategies(false);
        }
      };

      fetchIncompleteStrategies();
    } else {
      // Reset when switching back to byOrder mode
      setSelectedStrategy(null);
      setMissingOrders([]);
      setIncompleteStrategies([]);
    }
  }, [purchaseMode]);

  // Fetch missing orders when a strategy is selected
  const handleStrategySelect = async (strategy) => {
    playSoundEffect('/sounds/click.mp3');
    setSelectedStrategy(strategy);
    setSelectedOrder(null);
    setPurchaseMessage(null);

    try {
      setLoadingStrategies(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const currentLanguage = i18nInstance.language || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');

      const res = await fetch(`${API_URL}/bank/strategy/${strategy.id}/missing-orders`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': normalizedLanguage,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setMissingOrders(data.missingOrders || []);
      } else {
        console.error('Failed to fetch missing orders');
        setMissingOrders([]);
      }
    } catch (err) {
      console.error('Error fetching missing orders:', err);
      setMissingOrders([]);
    } finally {
      setLoadingStrategies(false);
    }
  };

  // Filter orders based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const filtered = orders.filter(order =>
      order.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOrders(filtered);
  }, [searchTerm, orders]);

  const getOrderPrice = (order) => {
    if (!order || !order.rarity) return 0;
    const rarity = order.rarity.toLowerCase();
    return RARITY_PRICES[rarity] || RARITY_PRICES.common;
  };

  const handleOrderSelect = (order) => {
    playSoundEffect('/sounds/click.mp3');
    setSelectedOrder(order);
    setPurchaseMessage(null); // Clear any previous messages
  };

  const handlePurchase = async () => {
    if (!selectedOrder) {
      setPurchaseMessage({ type: 'error', text: t('bank.selectOrderToPurchase') });
      return;
    }

    // Check if already at max copies
    if (selectedOrder.atMaxCopies) {
      const maxCount = selectedOrder.maxCount || (selectedOrder.is_unique ? 1 : 3);
      setPurchaseMessage({ 
        type: 'error', 
        text: t('bank.alreadyOwnedMax', { count: maxCount }) 
      });
      sayRobotMessage('atMax');
      return;
    }

    const price = getOrderPrice(selectedOrder);
    if (billyBucks < price) {
      setPurchaseMessage({ type: 'error', text: t('bank.insufficientBucks') });
      sayRobotMessage('insufficient');
      return;
    }

    setPurchasing(true);
    setPurchaseMessage(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPurchaseMessage({ type: 'error', text: t('bank.mustBeLoggedInPurchase') });
        setPurchasing(false);
        return;
      }

      const res = await fetch(`${API_URL}/bank/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orderId: selectedOrder.id }),
      });

      const data = await res.json();

      if (res.ok) {
        playSoundEffect('/sounds/click.mp3');
        setBillyBucks(data.newBalance);
        sayRobotMessage('purchaseSuccess');
        
        // Refresh data based on mode
        if (purchaseMode === 'byOrder') {
          // Refresh orders list to update ownership counts
          const currentLanguage = i18nInstance.language || 'en-us';
          const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
          const ordersRes = await fetch(`${API_URL}/bank/orders?lang=${encodeURIComponent(normalizedLanguage)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept-Language': normalizedLanguage,
            },
          });
          if (ordersRes.ok) {
            const ordersData = await ordersRes.json();
            setOrders(ordersData);
            setFilteredOrders(ordersData);
          }
          
          // Clear selection after successful purchase in byOrder mode
          setTimeout(() => {
            setSelectedOrder(null);
            setPurchaseMessage(null);
          }, 2000);
        } else if (purchaseMode === 'completeStrategy' && selectedStrategy) {
          // Refresh missing orders for the selected strategy
          const currentLanguage = i18nInstance.language || 'en-us';
          const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
          
          const res = await fetch(`${API_URL}/bank/strategy/${selectedStrategy.id}/missing-orders`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept-Language': normalizedLanguage,
            },
          });

          if (res.ok) {
            const data = await res.json();
            const updatedMissingOrders = data.missingOrders || [];
            setMissingOrders(updatedMissingOrders);
            
            // Find the currently selected order in the updated list
            const currentOrderInList = updatedMissingOrders.find(o => o.id === selectedOrder.id);
            
            if (currentOrderInList && currentOrderInList.missingCount > 0) {
              // Order still needs more copies - keep it selected
              setSelectedOrder(currentOrderInList);
            } else if (updatedMissingOrders.length > 0) {
              // Order is complete, select the next missing order
              setSelectedOrder(updatedMissingOrders[0]);
            } else {
              // All orders are complete - clear selection
              setSelectedOrder(null);
            }
          }
          
          // Clear success message after a delay
          setTimeout(() => {
            setPurchaseMessage(null);
          }, 2000);
        }
      } else {
        setPurchaseMessage({ type: 'error', text: data.error || t('bank.failedToPurchase') });
      }
    } catch (err) {
      console.error('Error purchasing order:', err);
      setPurchaseMessage({ type: 'error', text: t('bank.networkError') });
    } finally {
      setPurchasing(false);
    }
  };

  const canPurchase = selectedOrder && 
    !selectedOrder.atMaxCopies && 
    billyBucks >= getOrderPrice(selectedOrder) && 
    !purchasing;

  /**
   * Plays the gamble build-up sound and resolves when it ENDS (or fails).
   * If the user has sounds disabled or the page is hidden, falls back to a fixed
   * anticipation delay so the surprise animation still gets time to play.
   * Hard 8s safety cap so a stuck audio element can never block the reveal forever.
   */
  const enrichGamblePrizes = async (prizes) => {
    const token = localStorage.getItem('token');
    if (!token) return prizes || [];
    return Promise.all((prizes || []).map(async (prize) => {
      if (prize.type === 'order' && prize.id) {
        try {
          const orderRes = await fetch(`${API_URL}/order/${prize.id}/full`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (orderRes.ok) {
            const orderData = await orderRes.json();
            return { ...prize, orderData };
          }
        } catch (err) {
          console.error('Error fetching order data:', err);
        }
      }
      return prize;
    }));
  };

  const playGambleBuildupAndWaitForEnd = () => {
    return new Promise((resolve) => {
      const FALLBACK_MS = 1800;
      const SAFETY_MS = 8000;

      if (typeof document !== 'undefined' && document.hidden) {
        setTimeout(resolve, FALLBACK_MS);
        return;
      }
      const volume = getEffectiveSoundOutputVolume(1);
      if (volume === null) {
        setTimeout(resolve, FALLBACK_MS);
        return;
      }

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      try {
        const audio = new Audio('/sounds/gamble.mp3');
        audio.volume = volume;
        audio.addEventListener('ended', finish, { once: true });
        audio.addEventListener('error', finish, { once: true });
        audio.play().catch((err) => {
          console.debug('Gamble buildup audio failed:', err);
          setTimeout(finish, FALLBACK_MS);
        });
        setTimeout(finish, SAFETY_MS);
      } catch (e) {
        console.debug('Gamble buildup audio init failed:', e);
        setTimeout(finish, FALLBACK_MS);
      }
    });
  };

  const handleGamble = async () => {
    if (billyBucks < 3000) {
      setGambleResult({ success: false, message: t('bank.insufficientBucksGamble') });
      sayRobotMessage('gambleInsufficient');
      return;
    }

    setGambling(true);
    setGambleResult(null);

    // Kick off the build-up sound + the API call in parallel. The reveal waits
    // for BOTH so the user always sees the surprise/preparation animation
    // through the full duration of gamble.mp3.
    const buildupPromise = playGambleBuildupAndWaitForEnd();

    const apiPromise = (async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        return { ok: false, error: t('bank.mustBeLoggedInGamble') };
      }
      try {
        const res = await fetch(`${API_URL}/bank/gamble`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        if (!res.ok) {
          return { ok: false, error: data.error || t('bank.failedToGamble') };
        }
        const enrichedPrizes = await enrichGamblePrizes(data.prizes);
        return { ok: true, data, enrichedPrizes };
      } catch (err) {
        console.error('Error gambling:', err);
        return { ok: false, error: t('bank.networkError') };
      }
    })();

    let apiResult;
    try {
      [apiResult] = await Promise.all([apiPromise, buildupPromise]);
    } catch (err) {
      console.error('Gamble flow error:', err);
      setGambleResult({ success: false, message: t('bank.networkError') });
      setGambling(false);
      return;
    }

    if (!apiResult || !apiResult.ok) {
      setGambleResult({ success: false, message: (apiResult && apiResult.error) || t('bank.failedToGamble') });
      setGambling(false);
      return;
    }

    const { data, enrichedPrizes } = apiResult;
    // Mixed results count as a WIN: success sound plays whenever at least one
    // prize is non-"nothing", even if other prize slots came up empty.
    const wonAnything = enrichedPrizes.some((p) => p && p.type !== 'nothing');

    setBillyBucks(data.newBalance);
    setGambleResult({
      success: true,
      message: t('bank.gambleSuccessful'),
      prizes: enrichedPrizes,
    });
    sayRobotMessage(wonAnything ? 'gambleWin' : 'gambleEmpty');
    playSoundEffect(wonAnything ? '/sounds/gamble_success.mp3' : '/sounds/gamble_loss.mp3');
    if (data.achievementUnlocked) {
      setAchievementUnlocked(data.achievementUnlocked);
    }
    setGambling(false);
  };

  const handleDevMockEpicGambleWin = async () => {
    if (!isDevPlayer1) return;

    setGambling(true);
    setGambleResult(null);

    try {
      await playGambleBuildupAndWaitForEnd();

      const token = localStorage.getItem('token');
      if (!token) {
        setGambleResult({ success: false, message: t('bank.mustBeLoggedInGamble') });
        setGambling(false);
        return;
      }

      const epics = orders.filter((o) => String(o.rarity || '').toLowerCase() === 'epic');
      let candidateId = epics.length > 0
        ? epics[Math.floor(Math.random() * epics.length)].id
        : null;

      let orderData = null;
      if (candidateId != null) {
        try {
          const orderRes = await fetch(`${API_URL}/order/${candidateId}/full`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (orderRes.ok) {
            orderData = await orderRes.json();
          }
        } catch (err) {
          console.error('Dev epic preview: order full fetch failed:', err);
        }
      }

      if (!orderData) {
        orderData = { ...DEV_BANK_EPIC_PREVIEW_FALLBACK_ORDER };
      }

      const prize = {
        type: 'order',
        id: orderData.id,
        name: orderData.name,
        rarity: (orderData.rarity || 'epic').toLowerCase(),
        orderData,
      };

      setGambleResult({
        success: true,
        message: t('bank.gambleSuccessful'),
        prizes: [prize],
      });
      sayRobotMessage('gambleWin');
      playSoundEffect('/sounds/gamble_success.mp3');
    } catch (err) {
      console.error('Dev mock epic preview error:', err);
      setGambleResult({ success: false, message: t('bank.networkError') });
    } finally {
      setGambling(false);
    }
  };

  const handleMobileBankTab = (tab) => {
    playSoundEffect('/sounds/click.mp3');
    setMobileBankTab(tab);
    if (tab === 'buyOrder') {
      setPurchaseMode('byOrder');
      setSelectedStrategy(null);
      setSelectedOrder(null);
      setMissingOrders([]);
    } else if (tab === 'completeStrategy') {
      setPurchaseMode('completeStrategy');
      setSelectedOrder(null);
    }
  };

  const handleConvertDuplicators = async (fromRarity) => {
    if (orderDuplicators[fromRarity] < 5) {
      setConvertMessage({ 
        type: 'error', 
        text: t('bank.insufficientDuplicators', { rarity: bankRarityLabel(fromRarity, t), required: 5 }) 
      });
      sayRobotMessage('convertInsufficient');
      return;
    }

    setConverting(true);
    setConvertMessage(null);
    playSoundEffect('/sounds/upgrade_duplicators.mp3');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setConvertMessage({ type: 'error', text: t('bank.mustBeLoggedIn') });
        setConverting(false);
        return;
      }

      const res = await fetch(`${API_URL}/bank/convert-duplicators`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fromRarity }),
      });

      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        console.error('Error parsing JSON response:', jsonErr);
        setConvertMessage({ 
          type: 'error', 
          text: `Server error: ${res.status} ${res.statusText}` 
        });
        return;
      }

      if (res.ok) {
        setOrderDuplicators(data.duplicators);
        sayRobotMessage('convertSuccess');
      } else {
        setConvertMessage({ 
          type: 'error', 
          text: data.error || data.details || t('bank.conversionFailed') 
        });
      }
    } catch (err) {
      console.error('Error converting duplicators:', err);
      console.error('Error details:', err.message, err.stack);
      setConvertMessage({ 
        type: 'error', 
        text: err.message || t('bank.networkError') 
      });
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="bank-viewport-wrapper">
      {achievementUnlocked && (
        <AchievementUnlockedToast
          titleId={achievementUnlocked.titleId}
          titleName={achievementUnlocked.titleName}
          color={achievementUnlocked.color}
          achievementKey={achievementUnlocked.achievementKey}
          onClose={() => setAchievementUnlocked(null)}
        />
      )}
      {pendingToast && (
        <AchievementUnlockedToast
          titleId={pendingToast.titleId}
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      )}
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="bank-fullscreen-background"
        style={{ backgroundImage: 'url(/images/billy_buck_bank.jpg)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`bank-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className={`bank-container${isContainerMobile ? ' bank-container--mobile-landscape' : ''}`}>
          {isContainerMobile ? (
            <header className="bank-mobile-landscape-chrome">
              <div className="bank-mobile-landscape-chrome__icons">
                <FriendsIcon />
                <SettingsMenu onLogout={() => {
                  playSoundEffect('/sounds/click.mp3');
                  onLogout();
                  navigate('/');
                }} />
                <span className="bank-top-bar-user">{typeof user === 'string' ? user : (user?.username ?? '')}</span>
              </div>
              <div className="bank-total-balance bank-total-balance--mobile-landscape">
                <span className="bank-balance-label">{t('bank.totalBalance')}</span>
                <span className="bank-balance-value">{billyBucks.toLocaleString()} {t('bank.billyBucks')}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  playSoundEffect('/sounds/click.mp3');
                  navigate('/welcome');
                }}
                className="back-button bank-mobile-landscape-back"
              >
                {t('common.back')}
              </button>
            </header>
          ) : (
            <>
              <div
                className="bank-top-bar"
                style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}
              >
                <FriendsIcon />
                <SettingsMenu onLogout={() => {
                  playSoundEffect('/sounds/click.mp3');
                  onLogout();
                  navigate('/');
                }} />
                <span className="bank-top-bar-user">{typeof user === 'string' ? user : (user?.username ?? '')}</span>
              </div>
              <button onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                navigate('/welcome');
              }} className="back-button">
                {t('common.back')}
              </button>
              {/* Robot teller speech bubble — small paper note tacked above the robot.
                  Replaces the dead-center balance pill; balance lives on the desk's deposit slip now. */}
              <div
                className="bank-robot-bubble"
                key={robotMessageKey}
                role="status"
                aria-live="polite"
              >
                {t(`bank.robot.${robotMessageKey}`)}
              </div>
            </>
          )}

      {isContainerMobile && (
        <div className="bank-mobile-tabs" role="tablist" aria-label={t('bank.title')}>
          <button
            type="button"
            role="tab"
            aria-selected={mobileBankTab === 'buyOrder'}
            className={`bank-mobile-tab ${mobileBankTab === 'buyOrder' ? 'active' : ''}`}
            onClick={() => handleMobileBankTab('buyOrder')}
          >
            {t('bank.buyOrder')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileBankTab === 'completeStrategy'}
            className={`bank-mobile-tab ${mobileBankTab === 'completeStrategy' ? 'active' : ''}`}
            onClick={() => handleMobileBankTab('completeStrategy')}
          >
            {t('bank.completeStrategy')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileBankTab === 'convert'}
            className={`bank-mobile-tab ${mobileBankTab === 'convert' ? 'active' : ''}`}
            onClick={() => handleMobileBankTab('convert')}
          >
            {t('bank.convertDuplicators')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mobileBankTab === 'gamble'}
            className={`bank-mobile-tab ${mobileBankTab === 'gamble' ? 'active' : ''}`}
            onClick={() => handleMobileBankTab('gamble')}
          >
            {t('bank.gamble')}
          </button>
        </div>
      )}

      {/* Bank Content — the parchment "Purchase Ledger" the robot teller pushed across the desk. */}
      <div
        className={`bank-content${
          isContainerMobile && (mobileBankTab === 'convert' || mobileBankTab === 'gamble')
            ? ' bank-mobile-panel-hidden'
            : ''
        }${isContainerMobile && mobileBankTab !== 'convert' && mobileBankTab !== 'gamble' ? ' bank-content--mobile-landscape' : ''}`}
      >
        {/* Hand-painted blue ledger heading — desktop only (mobile hides via CSS). */}
        <div className="bank-ledger-heading" aria-hidden="true">
          {t('bank.ledgerHeading')}
          <span className="bank-ledger-heading-subtitle">{t('bank.ledgerSubtitle')}</span>
        </div>

        {/* Deposit slip on the desk: stack of cash + balance. Replaces the old top-center pill. */}
        <div className="bank-deposit-slip" aria-label={t('bank.totalBalance')}>
          <img
            src="/images/billy_bucks.png"
            alt=""
            className="bank-deposit-slip__cash"
            aria-hidden="true"
          />
          <div className="bank-deposit-slip__text">
            <span className="bank-deposit-slip__amount">
              {billyBucks.toLocaleString()} {t('bank.billyBucks')}
            </span>
            <span className="bank-deposit-slip__label">{t('bank.totalBalance')}</span>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="bank-mode-selection">
          <div className="bank-mode-buttons">
            <button
              className={`bank-mode-button ${purchaseMode === 'byOrder' ? 'active' : ''}`}
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setPurchaseMode('byOrder');
                setSelectedOrder(null);
                setSelectedStrategy(null);
                setMissingOrders([]);
              }}
            >
              {t('bank.buyOrder')}
            </button>
            <button
              className={`bank-mode-button ${purchaseMode === 'completeStrategy' ? 'active' : ''}`}
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setPurchaseMode('completeStrategy');
                setSelectedOrder(null);
              }}
            >
              {t('bank.completeStrategy')}
            </button>
          </div>
        </div>

        {purchaseMode === 'byOrder' ? (
          <div
            className={`bank-purchase-main${isContainerMobile ? ' bank-purchase-main--mobile-scroll' : ''}`}
          >
            {/* Search Bar */}
            <div className="bank-search-container">
              <input
                id="buy-order-search"
                type="text"
                className="bank-search-input"
                placeholder={t('bank.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Order List */}
            {loading ? (
              <div className="bank-loading">{t('bank.loadingOrders')}</div>
            ) : (
              <div
                className={`bank-order-list${isContainerMobile ? ' bank-order-list--mobile-scroll' : ''}`}
              >
                {filteredOrders.length === 0 ? (
                  <div className="bank-no-results">
                    {searchTerm ? t('bank.noOrdersFound', { searchTerm }) : t('bank.noOrdersAvailable')}
                  </div>
                ) : (
                  filteredOrders.map((order) => (
                    <div
                      key={order.id}
                      className={`bank-order-item ${selectedOrder?.id === order.id ? 'selected' : ''} ${order.atMaxCopies ? 'at-max' : ''}`}
                      onClick={() => handleOrderSelect(order)}
                    >
                      <span className="bank-order-name">{order.name}</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <span className={`bank-order-rarity rarity-${(order.rarity || 'common').toLowerCase()}`}>{bankRarityLabel(order.rarity, t)}</span>
                        {order.atMaxCopies && (
                          <span className="bank-order-max-indicator">
                            {t('bank.owned', { owned: order.ownedCount, max: order.maxCount })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            className={`bank-purchase-main${isContainerMobile ? ' bank-purchase-main--mobile-scroll' : ''}`}
          >
            {/* Strategy List */}
            {loadingStrategies ? (
              <div className="bank-loading">{t('bank.loadingStrategies')}</div>
            ) : (
              <>
                {selectedStrategy ? (
                  <>
                    {/* Back to Strategy List Button */}
                    <button
                      className="bank-back-button"
                      onClick={() => {
                        playSoundEffect('/sounds/click.mp3');
                        setSelectedStrategy(null);
                        setMissingOrders([]);
                        setSelectedOrder(null);
                      }}
                    >
                      ← {t('bank.backToStrategies')}
                    </button>
                    <div className="bank-strategy-header">
                      <h3>{selectedStrategy.name}</h3>
                      {selectedStrategy.winCount > 0 && (
                        <span className="bank-strategy-wins">{t('bank.wins', { count: selectedStrategy.winCount })}</span>
                      )}
                    </div>
                    {/* Missing Orders List */}
                    <div
                      className={`bank-order-list${isContainerMobile ? ' bank-order-list--mobile-scroll' : ''}`}
                    >
                      {missingOrders.length === 0 ? (
                        <div className="bank-no-results">{t('bank.noMissingOrders')}</div>
                      ) : (
                        missingOrders.map((order) => (
                          <div
                            key={order.id}
                            className={`bank-order-item ${selectedOrder?.id === order.id ? 'selected' : ''} ${order.atMaxCopies ? 'at-max' : ''}`}
                            onClick={() => handleOrderSelect(order)}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <span className="bank-order-name">{order.name}</span>
                              <span className="bank-order-missing">
                                {t('bank.missing', { missing: order.missingCount, required: order.requiredCount, owned: order.ownedCount })}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <span className={`bank-order-rarity rarity-${(order.rarity || 'common').toLowerCase()}`}>{bankRarityLabel(order.rarity, t)}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Spacer to match search bar height in buy order mode */}
                    <div className="bank-search-container" style={{ visibility: 'hidden', pointerEvents: 'none' }}>
                      <input type="text" className="bank-search-input" />
                    </div>
                    <div
                      className={`bank-order-list${isContainerMobile ? ' bank-order-list--mobile-scroll' : ''}`}
                    >
                      {incompleteStrategies.length === 0 ? (
                        <div className="bank-no-results">{t('bank.noIncompleteStrategies')}</div>
                      ) : (
                        incompleteStrategies.map((strategy) => (
                          <div
                            key={strategy.id}
                            className={`bank-order-item ${selectedStrategy?.id === strategy.id ? 'selected' : ''}`}
                            onClick={() => handleStrategySelect(strategy)}
                          >
                            <span className="bank-order-name">{strategy.name}</span>
                            {strategy.winCount > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <span className="bank-strategy-wins">{t('bank.wins', { count: strategy.winCount })}</span>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {(purchaseMode === 'byOrder' || (purchaseMode === 'completeStrategy' && selectedStrategy)) && (
          <div className="bank-purchase-footer-group">
            <div className="bank-cost-section">
              <div className="bank-cost-line">
                <span className="bank-cost-label">{t('bank.totalCost')}</span>
                <span className="bank-cost-value">
                  {selectedOrder ? getOrderPrice(selectedOrder).toLocaleString() : '0'} {t('bank.billyBucks')}
                </span>
              </div>
            </div>

            {purchaseMessage && (
              <div className={`bank-message ${purchaseMessage.type}`}>
                {purchaseMessage.text}
              </div>
            )}

            {selectedOrder && selectedOrder.atMaxCopies && (
              <div className="bank-message error">
                {t('bank.alreadyOwnedMax', { count: selectedOrder.maxCount })}
              </div>
            )}

            <button
              className="bank-buy-button"
              onClick={handlePurchase}
              disabled={!canPurchase}
            >
              {purchasing ? t('bank.processing') : selectedOrder?.atMaxCopies ? t('bank.alreadyOwnedMaxButton') : t('bank.authorize')}
            </button>
          </div>
        )}
      </div>

      {/* Duplicator Conversion — brass stamping press sitting on the right of the desk. */}
      <div
        className={`bank-conversion-content${isContainerMobile && mobileBankTab !== 'convert' ? ' bank-mobile-panel-hidden' : ''}`}
      >
        <div className="bank-conversion-header">
          <h2 className="bank-conversion-title">{t('bank.conversionPressTitle')}</h2>
          <span className="bank-conversion-subtitle">{t('bank.conversionPressSubtitle')}</span>
        </div>
        
        <div className="bank-conversion-grid">
          {/* Common to Rare */}
          <div className="bank-conversion-item">
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-common">{t('bank.common')}</div>
              <div className="bank-conversion-count">{orderDuplicators.common}</div>
            </div>
            <div className="bank-conversion-arrow">→</div>
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-rare">{t('bank.rare')}</div>
              <div className="bank-conversion-rate">{t('bank.conversionRate')}</div>
            </div>
            <button
              className="bank-convert-button"
              onClick={() => handleConvertDuplicators('common')}
              disabled={orderDuplicators.common < 5 || converting}
            >
              {converting ? t('bank.converting') : t('bank.convert')}
            </button>
          </div>

          {/* Rare to Epic */}
          <div className="bank-conversion-item">
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-rare">{t('bank.rare')}</div>
              <div className="bank-conversion-count">{orderDuplicators.rare}</div>
            </div>
            <div className="bank-conversion-arrow">→</div>
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-epic">{t('bank.epic')}</div>
              <div className="bank-conversion-rate">{t('bank.conversionRate')}</div>
            </div>
            <button
              className="bank-convert-button"
              onClick={() => handleConvertDuplicators('rare')}
              disabled={orderDuplicators.rare < 5 || converting}
            >
              {converting ? t('bank.converting') : t('bank.convert')}
            </button>
          </div>

          {/* Epic to Legendary */}
          <div className="bank-conversion-item">
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-epic">{t('bank.epic')}</div>
              <div className="bank-conversion-count">{orderDuplicators.epic}</div>
            </div>
            <div className="bank-conversion-arrow">→</div>
            <div className="bank-conversion-info">
              <div className="bank-conversion-rarity rarity-legendary">{t('bank.legendary')}</div>
              <div className="bank-conversion-rate">{t('bank.conversionRate')}</div>
            </div>
            <button
              className="bank-convert-button"
              onClick={() => handleConvertDuplicators('epic')}
              disabled={orderDuplicators.epic < 5 || converting}
            >
              {converting ? t('bank.converting') : t('bank.convert')}
            </button>
          </div>
        </div>

        {/* Conversion Message */}
        {convertMessage && (
          <div className={`bank-message ${convertMessage.type}`} style={{ marginTop: '15px' }}>
            {convertMessage.text}
          </div>
        )}
      </div>

      {/* Wager Drawer — rusted cash box the robot slides forward. (Mobile keeps the same component.) */}
      <div
        className={`bank-gamble-content${gambling ? ' bank-gamble-content--rolling' : ''}${isContainerMobile && mobileBankTab !== 'gamble' ? ' bank-mobile-panel-hidden' : ''}`}
      >
        <div className="bank-gamble-header">
          <h2 className="bank-gamble-title">{t('bank.wagerDrawerTitle')}</h2>
          <span className="bank-gamble-subtitle">{t('bank.wagerDrawerSubtitle')}</span>
        </div>
        
        <button
          className="bank-gamble-button"
          onClick={handleGamble}
          disabled={billyBucks < 3000 || gambling}
        >
          {gambling ? t('bank.rolling') : t('bank.betButton')}
        </button>

        {isDevPlayer1 && (
          <button
            type="button"
            className="bank-gamble-dev-mock-button"
            onClick={handleDevMockEpicGambleWin}
            disabled={gambling}
          >
            {t('bank.devMockEpicGambleButton')}
          </button>
        )}

        {/* Surprise/preparation animation while gamble.mp3 build-up plays. The reveal
            is gated by handleGamble waiting for the audio to end before setting gambleResult. */}
        {gambling && !gambleResult && (
          <div className="bank-gamble-preparing" aria-live="polite" aria-label={t('bank.preparingLabel')}>
            <div className="bank-gamble-preparing-cards">
              <div className="bank-gamble-preparing-card" aria-hidden="true">?</div>
              <div className="bank-gamble-preparing-card" aria-hidden="true">?</div>
              <div className="bank-gamble-preparing-card" aria-hidden="true">?</div>
            </div>
            <div className="bank-gamble-preparing-label">{t('bank.preparingLabel')}</div>
          </div>
        )}

        {gambleResult && (
          <div className="bank-gamble-result">
            {!gambleResult.success && (
              <div className={`bank-message error`}>
                {gambleResult.message}
              </div>
            )}
            {gambleResult.prizes && gambleResult.prizes.length > 0 ? (
              <div className="bank-gamble-prizes">
                <h3>{t('bank.prizesWon')}</h3>
                <div className="bank-gamble-prizes-grid">
                  {gambleResult.prizes.map((prize, index) => (
                    <div key={index} className="bank-gamble-prize-card">
                      {prize.type === 'billy_bucks' && (
                        <div className="bank-prize-billy-bucks">
                          <img 
                            src="/images/billy_bucks.png" 
                            alt={t('bank.billyBucks')} 
                            className="bank-prize-image"
                          />
                          <div className="bank-prize-amount">{prize.amount || 0}</div>
                          <div className="bank-prize-label">{t('bank.billyBucks')}</div>
                        </div>
                      )}
                      {prize.type === 'order' && prize.orderData && (
                        <div className={`reward-order-card rarity-${(prize.orderData.rarity || prize.rarity || 'common').toLowerCase()}`} style={{ position: 'relative' }}>
                          <OrderDesign
                            order={prize.orderData}
                            minimal={false}
                            noMargin={true}
                          />
                          <OrderCardBubbles rarity={prize.orderData.rarity || prize.rarity || 'common'} />
                        </div>
                      )}
                      {prize.type === 'order' && !prize.orderData && (
                        <div className="bank-prize-fallback">
                          {prize.name || t('bank.unknownOrder')} ({prize.rarity ? bankRarityLabel(prize.rarity, t) : t('bank.unknown')})
                        </div>
                      )}
                      {prize.type === 'duplicator' && (
                        <div className="bank-prize-duplicator">
                          <div className="bank-prize-duplicator-icon">DUP</div>
                          <div className="bank-prize-name">{prize.name || t('bank.unknownDuplicator')}</div>
                          <div className="bank-prize-rarity">{prize.rarity ? bankRarityLabel(prize.rarity, t) : t('bank.unknown')}</div>
                        </div>
                      )}
                      {prize.type === 'preparation_bonus' && (() => {
                        // Match by key - same as Profile.js
                        const feature = preparationBonusFeatures.find(f => f.key === prize.key);
                        const imageSrc = feature ? feature.image : '/images/team_leader_briefing.jpg'; // Fallback
                        return (
                          <div className="bank-prize-feature">
                            <img 
                              src={imageSrc} 
                              alt={feature ? t(feature.altI18nKey) : t('bank.preparationBonus')} 
                              className="preparation-bonus-image"
                              style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                            />
                            <div className="bank-prize-label">{t('bank.preparationBonus')}</div>
                            <div className="bank-prize-name">{prize.name || prize.key || t('bank.unknown')}</div>
                          </div>
                        );
                      })()}
                      {prize.type === 'diplomatic_accord' && (() => {
                        // Match by key - same as Profile.js
                        const prizeKey = prize.key;
                        console.log('Diplomatic accord prize:', prize, 'Looking for key:', prizeKey, 'Type:', typeof prizeKey);
                        console.log('Available features:', diplomaticAccordsFeatures.map(f => ({ key: f.key, type: typeof f.key })));
                        const feature = diplomaticAccordsFeatures.find(f => {
                          const match = f.key === prizeKey;
                          console.log(`Comparing "${f.key}" === "${prizeKey}": ${match}`);
                          return match;
                        });
                        console.log('Found feature:', feature);
                        if (!feature) {
                          console.error('No feature found for diplomatic_accord key:', prizeKey, 'Available keys:', diplomaticAccordsFeatures.map(f => f.key));
                        }
                        const imageSrc = feature ? feature.image : '/images/strategic_coordination.jpg'; // Fallback
                        console.log('Using image:', imageSrc);
                        return (
                          <div className="bank-prize-feature">
                            <img 
                              src={imageSrc} 
                              alt={feature ? t(feature.altI18nKey) : t('bank.diplomaticAccord')} 
                              className="preparation-bonus-image"
                              style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                              onError={(e) => {
                                console.error('Image failed to load:', imageSrc);
                                e.target.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('Image loaded successfully:', imageSrc);
                              }}
                            />
                            <div className="bank-prize-label">{t('bank.diplomaticAccord')}</div>
                            <div className="bank-prize-name">{prize.name || prize.key || t('bank.unknown')}</div>
                            {!feature && <div style={{ color: 'red', fontSize: '0.8rem' }}>Key: {prizeKey}</div>}
                          </div>
                        );
                      })()}
                      {prize.type === 'avatar' && (
                        <div className="bank-prize-fallback">
                          {t('bank.avatar')} {prize.name || prize.unlockKey || t('bank.unknown')}
                        </div>
                      )}
                      {prize.type === 'emote' && (
                        <div className="bank-prize-emote">
                          <div className="bank-prize-label">{t('bank.emote')}</div>
                          <div className="bank-prize-name">{prize.name || prize.unlockKey || t('bank.unknown')}</div>
                          {prize.speechBubble && (
                            <div className="bank-prize-emote-bubble">&ldquo;{prize.speechBubble}&rdquo;</div>
                          )}
                        </div>
                      )}
                      {prize.type === 'flashlight_color' && prize.color && (
                        <div className="bank-prize-flashlight">
                          <div
                            className="bank-prize-flashlight-swatch"
                            style={{
                              background: buildFlashlightSwatchBackground(
                                normalizeFlashlightColorForDisplay(prize.color)
                              ),
                            }}
                          />
                          <div className="bank-prize-label">
                            {t('afterBattle.flashlightUnlocked', {
                              color: t(
                                `profile.flashlight.colors.${normalizeFlashlightColorForDisplay(prize.color)}`,
                                { defaultValue: prize.color }
                              ),
                            })}
                          </div>
                        </div>
                      )}
                      {prize.type === 'building' && (
                        <div className="bank-prize-building">
                          {BANK_GAMBLE_WAR_BUILDING_IMAGES[prize.buildingId] && (
                            <img
                              src={BANK_GAMBLE_WAR_BUILDING_IMAGES[prize.buildingId]}
                              alt=""
                              className="bank-prize-building-image"
                            />
                          )}
                          <div className="bank-prize-label">{t('bank.warEffortBuilding')}</div>
                          <div className="bank-prize-name">{prize.name || prize.buildingId || t('bank.unknown')}</div>
                        </div>
                      )}
                      {prize.type === 'nothing' && (
                        <div className="bank-prize-nothing">
                          <div className="bank-prize-nothing-icon">{t('bank.emptyDrawerStamp')}</div>
                          <div className="bank-prize-label">{t('bank.nothing')}</div>
                          <div className="bank-prize-name">{t('bank.betterLuckNextTime')}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : gambleResult.success && (
              <div className="bank-message error" style={{ marginTop: '10px' }}>
                {t('bank.noPrizesGranted')}
              </div>
            )}
          </div>
        )}
      </div>

      {showTutorialBank && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_welcome_3.png"
          text={t('bank.tutorialText')}
          onContinue={async () => {
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'bank' }),
              });
              markSeenLocally('bank');
            }
            setShowTutorialBank(false);
          }}
          voiceKey="bank.tutorialText"
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default Bank;

